import { Children, useCallback, useMemo, useRef } from 'react';
import { Outlet, NavLink, useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { configuration, dialog, dom } from 'app/utils';
import { getUser, signout, useThunkDispatch } from 'app/state';
import { Avatar, Menu, MenuItem, MenuItemOptions, Ripple } from 'app/components/external';
import { INavbar, Logo, Navbar } from 'app/components';
import { Icon } from 'app/components/common';

const menuitem = (item: MenuItem, options: MenuItemOptions) => {
  const props: any = {
    className: dom.className(options.className, { 'active': (!item.url && item.data?.active) }),
    tabIndex: -1,
    'aria-hidden': true,
    'aria-haspopup': !!item.items?.length,
    'data-pc-section': 'action',

    onFocus: (e: FocusEvent) => e.stopPropagation(),
    onClick: options.onClick,

    children: Children.toArray([
      (item.icon && <Icon name={item.icon} className={dom.className(options.iconClassName, item.className, 'mr-3')} />),
      (<span className={dom.className(options.labelClassName, item.className)} data-pc-section="label">{item.label}</span>),
      (!!item.items?.length && <i className="p-icon p-submenu-icon pi pi-angle-down" />),
      (<Ripple />),
    ]),
  };

  return (item.url ? <NavLink to={item.url} { ...props } /> : <span { ...props } />);
};

const popupitem = (item: MenuItem, options: MenuItemOptions) => (
  <div className="p-menuitem-content" data-pc-section="content">
    { menuitem(item, options) }
  </div>
);

export default function Default() {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const dispatch = useThunkDispatch();

  const user = useSelector(getUser);

  const profile = useRef<Menu>(null);
  const toggle = useCallback((e: any) => { profile.current?.toggle(e); }, []);

  const items = useMemo(() => {
    const menu: MenuItem[] = [
      { id: 'dashboard', label: t('app.navbar.dashboard'), icon: 'mdi-dashboard', url: '/', template: menuitem },
      // { id: 'clients', label: t('app.navbar.clients'), icon: 'mdi-cloud', url: '/clients', template: menuitem },
      { id: 'countries', label: t('app.navbar.countries'), icon: 'mdi-flag', url: '/countries', template: menuitem },
      { id: 'professions', label: t('app.navbar.professions'), icon: 'mdi-business-center', url: '/professions', template: menuitem },
      { id: 'specialties', label: t('app.navbar.specialties'), icon: 'mdi-business-center', url: '/specialties', template: menuitem },
      { id: 'users', label: t('app.navbar.users'), icon: 'mdi-groups', url: '/users', template: menuitem },
      // { id: 'profiles', label: t('app.navbar.profiles'), icon: 'mdi-group', url: '/profiles', template: menuitem },
      { id: 'confirmations', label: t('app.navbar.confirmations'), icon: 'mdi-rule', url: '/confirmations', template: menuitem },
      { id: 'offers', label: t('app.navbar.offers'), icon: 'mdi-stacked-email', url: '/offers', template: menuitem },
      { id: 'reports', label: t('app.navbar.reports'), icon: 'mdi-report', url: '/reports', template: menuitem },
    ];

    const isActive = ({ items, url }: MenuItem): boolean => (items?.length ? items.some(isActive) : ((url ??= '/') !== '/' && pathname.startsWith(url)));
    const setActive = (items: MenuItem[] | MenuItem[][]): void => items.forEach(item => {
      if (Array.isArray(item)) return setActive(item);
      if (!item.id) return;

      item.data = { active: isActive(item) };
      if (item.items?.length) setActive(item.items);
    });

    setActive(menu);

    return menu;
  }, [pathname, t]);

  const account: MenuItem[] = useMemo(() => [
    { id: 'profile', label: (user?.username ?? t('app.navbar.account.guest')), url: '/profile', template: popupitem },
    { id: 'settings', label: t('app.navbar.account.settings'), template: popupitem },
    { separator: true },
    { id: 'version', label: t('app.navbar.account.version', { version: configuration.VERSION }), template: popupitem },
    { separator: true },
    { id: 'signout', label: t('app.navbar.account.signout'), className: 'text-red-600', command() {
      dialog.confirm({
        message: t('app.navbar.account.signout.question'),
        accept: { label: t('app.navbar.account.signout'), className: 'ml-2 p-button-danger' },
        reject: { label: t('app.cancel') },
      }).then(accepted => {
        if (accepted) dispatch(signout());
      });
    }, template: popupitem },
  ], [dispatch, t, user?.username]);

  const navbar: INavbar = { items,
    start: (
      <Link to="/" className="p-menuitem-link w-4rem mx-2 px-2 py-1 border-round overflow-hidden">
        <Logo className="h-2rem block" style={{ display: 'none' }} />
        <Ripple />
      </Link>
    ),
    end: (
      <div className="flex align-items-center opacity-70">
        <Avatar icon={<Icon name="mdi-person" />} size="normal" shape="circle" className="ml-3 mr-2 cursor-pointer" onClick={toggle} />
        {/*<Button label={user?.name ?? 'Guest'} size="small" text className="ml-2 text-base" onClick={toggle} />*/}
        <Menu ref={profile} model={account} popup popupAlignment="right" className="mt-2" />
      </div>
    ),
  };

  return (
    <div className="w-screen h-screen flex flex-column surface-100">
      <Navbar { ...navbar } />
      <div className="min-h-0 flex flex-row flex-auto">
        <Outlet />
      </div>
    </div>
  );
}
